'use client';

import { useState } from 'react';
import type { GetValidQuantityProps } from './getValidQuantity/getValidQuantity';
import { getValidQuantity } from './getValidQuantity/getValidQuantity';
import { getValidValues } from './getValidValues/getValidValues';
import { ActionButton } from './ActionButton/ActionButton.client';

const elementsHeight = '2.25rem';

export type QuantitySelectionProps = {
  /**
   * Sets the maximum value the quantity can be.
   * @default 10
   */
  max?: number;
  /**
   * Sets the minimum value the quantity can be.
   * @default 1
   */
  min?: number;
  /**
   * Sets mow much the quantity of the step will be increased/decreased on button click.
   * @default 1
   */
  step?: number;
  /**
   * Sets the initial quantity
   */
  quantity?: number;
};

export type QuantitySelectionUnmanagedProps = Omit<
  QuantitySelectionProps,
  'onUpdate' | 'quantity'
> &
  Required<Pick<QuantitySelectionProps, 'quantity'>> & {
    /**
     * callback method which will be triggered when the quantity has changed
     */
    onUpdate: (quantity: number) => void;
  };

const defaultValues: Required<Pick<QuantitySelectionProps, 'step' | 'min' | 'max'>> = {
  step: 1,
  min: 1,
  max: 10,
};

/**
 * <p>
 * This component is used to display some quantity. It comes with an increase and decrease button.
 * With the given props, you can adjust the <code>step</code> value with which the quantity will be decreased/increased.
 * You also can set a <code>min</code>/<code>max</code> value for the quantity and define the initial quantity with the <code>quantity</code> prop.
 * </p>
 * This is the unmanaged version of the component. It will not manage the quantity state itself, but will call the <code>onUpdate</code> callback with the new quantity value.
 * */
export const QuantitySelectionUnmanaged = ({
  step = defaultValues.step,
  min = defaultValues.min,
  max = defaultValues.max,
  quantity,
  onUpdate,
}: QuantitySelectionUnmanagedProps) => {
  const values = getValidValues({
    step,
    min,
    max,
  });
  const validatedQuantity = getValidQuantity({
    type: 'initial',
    currentQuantity: quantity || values.min,
    step: values.step,
    min: values.min,
    max: values.max,
  });

  const handleAction = (type: GetValidQuantityProps['type']) => {
    const next = getValidQuantity({
      currentQuantity: validatedQuantity,
      step: values.step,
      ...(type === 'increase'
        ? { type: 'increase', max: values.max }
        : { type: 'decrease', min: values.min }),
    });

    if (typeof onUpdate === 'function' && next !== validatedQuantity) {
      onUpdate(next);
    }
  };

  return (
    <div className="text-size-body-base flex items-center justify-start">
      <ActionButton
        type="Decrease"
        disabled={validatedQuantity <= values.min}
        onClick={() => {
          handleAction('decrease');
        }}
        height={elementsHeight}
        step={step}
      />
      <div
        style={{
          height: elementsHeight,
          width: '4.5rem',
          lineHeight: elementsHeight,
        }}
        className="border-color-grey-dark mx-1 overflow-hidden rounded border text-center select-none"
      >
        {validatedQuantity}
      </div>
      <ActionButton
        type="Increase"
        disabled={validatedQuantity >= values.max}
        onClick={() => {
          handleAction('increase');
        }}
        height={elementsHeight}
        step={step}
      />
    </div>
  );
};

/**
 * <p>
 * This component is used to display some quantity. It comes with an increase and decrease button.
 * With the given props, you can adjust the <code>step</code> value with which the quantity will be decreased/increased.
 * You also can set a <code>min</code>/<code>max</code> value for the quantity and define the initial quantity with the <code>quantity</code> prop.
 * </p>
 * */
export const QuantitySelection = ({
  step = defaultValues.step,
  min = defaultValues.min,
  max = defaultValues.max,
  quantity,
}: QuantitySelectionProps) => {
  const values = getValidValues({
    step,
    min,
    max,
  });
  const initialQuantity = getValidQuantity({
    type: 'initial',
    currentQuantity: quantity || values.min,
    step: values.step,
    min: values.min,
    max: values.max,
  });

  const [quantityValue, setQuantityValue] = useState(initialQuantity);
  const [lastInitialQuantity, setLastInitialQuantity] = useState(initialQuantity);

  // A changed prop resets the user's choice during render; an effect would render twice.
  if (initialQuantity !== lastInitialQuantity) {
    setLastInitialQuantity(initialQuantity);
    setQuantityValue(initialQuantity);
  }

  return (
    <QuantitySelectionUnmanaged
      step={step}
      min={min}
      max={max}
      quantity={quantityValue}
      onUpdate={setQuantityValue}
    />
  );
};
